import { WebLinksAddon } from '@xterm/addon-web-links'
import { useAppStore } from '../stores/appStore'
import { isRemoteClient } from './environment'
import { findLocalhostUrls, rewriteToTunnel, type TunnelTarget } from './localhostLinks'
import type { LinkTarget } from '../types'

/**
 * One click handler for every terminal's links.
 *
 * There were six byte-identical copies of this closure — ClaudeTerminal,
 * CodexTerminal, Terminal, GeminiTerminal, GrokTerminal, KimiTerminal — which
 * meant the localhost behaviour had to be added six times and could drift five
 * ways. They now share this.
 *
 * On the desktop a link opens an in-app browser tab, unless Settings → Browser
 * says to hand it to the system's default browser instead. On the relay client
 * `http://localhost:5173` is a lie — localhost is the phone, and the dev server
 * is on a machine somewhere else — so it is rewritten to the relay tunnel.
 */

/**
 * Resolves the relay tunnel for this client, or null when there isn't one.
 *
 * MUST be synchronous. The resolved URL is handed to `window.open()` inside
 * the click gesture, and Safari blocks a `window.open` that happens after an
 * await — so this cannot be a relay round-trip, and every `window.api` method
 * on the mobile client is a promise over the WebSocket. The mobile bundle
 * installs this from its connection store, which is mutable at runtime
 * (pair/unpair), so it is read at call time and never cached.
 */
export type TunnelResolver = () => TunnelTarget | null

let resolver: TunnelResolver | null = null

/** Installed by the mobile bundle once a relay channel exists. */
export function setTunnelResolver(fn: TunnelResolver | null): void {
  resolver = fn
}

function currentTunnel(): TunnelTarget | null {
  if (!isRemoteClient()) return null
  try {
    return resolver?.() ?? null
  } catch {
    // A broken resolver must not swallow the click — fall through to the
    // untunnelled behaviour rather than making the link do nothing.
    return null
  }
}

/**
 * Where the user wants links to open, read at click time.
 *
 * Synchronous for the same reason as `currentTunnel` — `window.open` has to
 * happen inside the click gesture — which is why the preference is mirrored
 * into the store at startup rather than read from settings here.
 */
function currentLinkTarget(): LinkTarget {
  try {
    return useAppStore.getState().openLinksIn
  } catch {
    // Never let a store problem eat the click; the in-app tab is the
    // behaviour every link had before this setting existed.
    return 'in-app'
  }
}

/**
 * The clicked URL, but only if the WHOLE of it is a loopback URL.
 *
 * `findLocalhostUrls` scans for loopback URLs anywhere inside a string, which
 * is right for terminal output and wrong for a URL the user just clicked.
 * Taking its first hit unanchored meant
 *
 *   https://auth.example.com/authorize?redirect_uri=http://localhost:8976/callback
 *
 * matched at offset 48 and the entire click was rewritten to the tunnel — so
 * on a relay client, clicking an OAuth device-login link (printed by exactly
 * the CLIs this app runs: Claude Code, gh, supabase, vercel) navigated to the
 * user's own dev server instead of the identity provider, and the sign-in
 * failed with nothing to explain it. On the desktop the same loose match set
 * the new tab's `port` from a redirect_uri the page has nothing to do with.
 */
function wholeUrlMatch(url: string): ReturnType<typeof findLocalhostUrls>[number] | null {
  const [match] = findLocalhostUrls(url)
  if (!match || match.start !== 0 || match.end !== url.length) return null
  return match
}

/**
 * Where a clicked terminal link should actually go.
 *
 * Exported for testing: this is the whole decision, and it is worth being able
 * to assert on it without standing up an xterm.
 */
export function resolveTerminalUrl(
  url: string,
  tunnel: TunnelTarget | null,
  linkTarget: LinkTarget = 'in-app',
): { url: string; external: boolean } {
  const match = wholeUrlMatch(url)
  // 'system' is the user asking for their own browser (Settings → Browser), so
  // it wins for anything that is not going through the tunnel. A tunnelled URL
  // is external either way, so the two settings never disagree.
  if (!match) return { url, external: linkTarget === 'system' }
  const tunnelled = rewriteToTunnel(match, tunnel)
  if (!tunnelled) return { url, external: linkTarget === 'system' }
  // External because the tunnel is a different origin to the app, deliberately
  // so — see the note in the relay worker about what a dev server's own
  // JavaScript could read out of localStorage if it shared this one.
  return { url: tunnelled, external: true }
}

function openInAppTab(url: string): void {
  const s = useAppStore.getState()
  const pid = s.activeProjectId
  if (!pid) {
    // No project to hang a tab on — a first-run user, or a share recipient,
    // for whom nothing sets activeProjectId. Dropping the click here made a
    // link that underlines on hover do literally nothing, with no tab, no
    // window and no console warning. Hand it to the browser instead.
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  // Anchored for the same reason as resolveTerminalUrl: an embedded
  // redirect_uri must not decide this tab's port.
  const match = wholeUrlMatch(url)
  s.addTab({
    id: crypto.randomUUID(),
    type: 'browser',
    projectId: pid,
    folderId: s.activeFolderId || '',
    title: url.replace(/^https?:\/\//, '').slice(0, 30),
    // The six copies all hardcoded `port: 0`, which left BrowserView's port
    // dropdown (BrowserView.tsx reads `tabData?.port ?? 3000`) showing the
    // wrong port for every link opened from a terminal. We have already
    // parsed it, so pass the real one.
    data: { url, port: match?.port ?? 0 },
    closable: true,
  })
}

/**
 * The addon every terminal loads. Drop-in for `new WebLinksAddon(handler)`.
 */
export function createTerminalLinksAddon(): WebLinksAddon {
  return new WebLinksAddon((_e, url) => {
    const { url: target, external } = resolveTerminalUrl(url, currentTunnel(), currentLinkTarget())
    if (external) {
      // noopener: the tunnelled page must not get a handle on this window.
      window.open(target, '_blank', 'noopener,noreferrer')
      return
    }
    openInAppTab(target)
  })
}
